import xlsx from "json-as-xlsx";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";

import { ActivityButton, BreadCrumb } from "../../components/Common";
import { CourseSelect, GradeSelect } from "../../components";
import { useGetSubscriptionReportMutation } from "../../store/api/reportApi";
import {
  ParentsWithActiveChildSubscription,
  ParentsWithoutActiveChildSubscription,
  StudentsWithActiveSubscription,
  StudentsWithoutActiveSubscription,
  SubscriptionReportRequest,
} from "../../interfaces/reports";

type TProps = RouteComponentProps & {};
export const SubscriptionReportPage: React.FC<TProps> = () => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "subscriptionReport",
  })}`;

  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [filterTypeOfUser, setFilterTypeOfUser] = useState<string>("student");
  const [filterSubType, setFilterSubType] = useState<
    "subscription" | "non-subscription"
  >("subscription");
  const [request, setRequest] = useState<SubscriptionReportRequest>({
    userType: "student",
    subType: "subscription",
  });

  const [
    getSubscriptionReport,
    {
      data: responseSubscriptionReport,
      isSuccess: successSubscriptionReport,
      isLoading: isLoadingReport,
      reset: resetSubscriptionReport,
    },
  ] = useGetSubscriptionReportMutation();

  useEffect(() => {
    if (successSubscriptionReport && responseSubscriptionReport) {
      const now = new Date();
      let fileName = "Subscription Report";
      let settings = {
        fileName: `No Subscription Students ${now.getDate()}-${
          now.getMonth() + 1
        }-${now.getFullYear()}`,
        extraLength: 3,
        writeMode: "writeFile",
        writeOptions: {},
      };
      let reportResponse;
      let data: any[] = [];
      if (filterTypeOfUser === "student") {
        if (filterSubType === "subscription") {
          fileName = `Students With Active Subscription Report ${now.getDate()}-${
            now.getMonth() + 1
          }-${now.getFullYear()}`;

          reportResponse =
            responseSubscriptionReport as StudentsWithActiveSubscription[];
          data = [
            {
              sheet: "sheet 1",
              columns: [
                { label: "Id", value: "id" },
                { label: "Name", value: "name" },
                { label: "Gender", value: "gender" },
                { label: "Phone Number", value: "phoneNumber" },
                { label: "Course Name", value: "courseName" },
                { label: "Grade Name", value: "gradeName" },
                {
                  label: "Created Date",
                  value: "createdDate",
                  format: "dd-mm-yy hh:mm",
                },
              ],
              content: reportResponse.map((item) => ({
                id: item.userId,
                name: item.userNameEn,
                gender: item.gender,
                phoneNumber: item.phoneNumber,
                courseName: item.courseName,
                gradeName: item.gradeName,
                createdDate: item.createdDate,
              })),
            },
          ];
        }
        if (filterSubType === "non-subscription") {
          fileName = `Students Without Active Subscription Report ${now.getDate()}-${
            now.getMonth() + 1
          }-${now.getFullYear()}`;
          reportResponse =
            responseSubscriptionReport as StudentsWithoutActiveSubscription[];
          data = [
            {
              sheet: "sheet 1",
              columns: [
                { label: "Id", value: "id" },
                { label: "Name", value: "name" },
                { label: "Gender", value: "gender" },
                { label: "Phone Number", value: "phoneNumber" },
                { label: "Course Name", value: "courseName" },
                { label: "Grade Name", value: "gradeName" },
                {
                  label: "Created Date",
                  value: "createdDate",
                  format: "dd-mm-yy hh:mm",
                },
              ],
              content: reportResponse.map((item) => ({
                id: item.userId,
                name: item.userNameEn,
                gender: item.gender,
                phoneNumber: item.phoneNumber,
                courseName: item.courseName,
                gradeName: item.gradeName,
                createdDate: item.createdDate,
              })),
            },
          ];
        }
      }
      if (filterTypeOfUser === "parent") {
        if (filterSubType === "subscription") {
          fileName = `Parents With Active Child Subscription Report ${now.getDate()}-${
            now.getMonth() + 1
          }-${now.getFullYear()}`;
          reportResponse =
            responseSubscriptionReport as ParentsWithActiveChildSubscription[];
          data = [
            {
              sheet: "sheet 1",
              columns: [
                { label: "Id", value: "id" },
                { label: "Name", value: "name" },
                { label: "Gender", value: "gender" },
                { label: "Phone Number", value: "phoneNumber" },
                { label: "Course Name", value: "courseName" },
                { label: "Grade Name", value: "gradeName" },
                {
                  label: "Created Date",
                  value: "createdDate",
                  format: "dd-mm-yy hh:mm",
                },
              ],
              content: reportResponse.map((item) => ({
                id: item.userId,
                name: item.userNameEn,
                gender: item.gender,
                phoneNumber: item.phoneNumber,
                courseName: item.courseName,
                gradeName: item.gradeName,
                createdDate: item.createdDate,
              })),
            },
          ];
        }
        if (filterSubType === "non-subscription") {
          fileName = `Parents Without Active Child Subscription Report ${now.getDate()}-${
            now.getMonth() + 1
          }-${now.getFullYear()}`;
          reportResponse =
            responseSubscriptionReport as ParentsWithoutActiveChildSubscription[];
          data = [
            {
              sheet: "sheet 1",
              columns: [
                { label: "Id", value: "id" },
                { label: "Name", value: "name" },
                { label: "Gender", value: "gender" },
                { label: "Phone Number", value: "phoneNumber" },
                { label: "Course Name", value: "courseName" },
                { label: "Grade Name", value: "gradeName" },
                {
                  label: "Created Date",
                  value: "createdDate",
                  format: "dd-mm-yy hh:mm",
                },
              ],
              content: reportResponse.map((item) => ({
                id: item.userId,
                name: item.userNameEn,
                gender: item.gender,
                phoneNumber: item.phoneNumber,
                courseName: item.courseName,
                gradeName: item.gradeName,
                createdDate: item.createdDate,
              })),
            },
          ];
        }
      }
      settings = { ...settings, fileName };
      xlsx(data, settings, () => resetSubscriptionReport());
    }
  }, [successSubscriptionReport]);

  const onChangeCourseFilter = (value: string) => {
    setFilterGradeId("");
    setFilterCourseId(value);
    setRequest({
      ...request,
      courseId: value,
    });
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setRequest({
      ...request,
      gradeId: value,
    });
  };

  const onChangeTypeOfUserFilter = (value: "student" | "parent") => {
    setFilterTypeOfUser(value);
    setRequest({
      ...request,
      userType: value,
    });
  };

  const onChangeSubTypeFilter = (
    value: "subscription" | "non-subscription",
  ) => {
    setFilterSubType(value);
    setRequest({
      ...request,
      subType: value,
    });
  };

  const onChangeGenderFilter = (value: string) => {
    setFilterGenderId(value);
    setRequest({
      ...request,
      gender: value,
    });
  };

  const onClearFilters = () => {
    setFilterCourseId("");
    setFilterGradeId("");
    setFilterGenderId("");
    setRequest({
      ...request,
      courseId: "",
      gradeId: "",
      gender: "",
    });
  };

  const generateReport = () => {
    if (!request.userType || !request.subType) {
      return;
    }
    getSubscriptionReport(request);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            link="/reports"
            title={t("subscriptionReport")}
            pageTitle={t("reports")}
          />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <label className="form-label">
                          {t("userType")}
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          value={filterTypeOfUser}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeTypeOfUserFilter(event.target.value)
                          }
                        >
                          <option value="student">{t("student")}</option>
                          <option value="parent">{t("parent")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <label className="form-label">
                          {t("subscription")}
                          <span className="text-danger">*</span>
                        </label>
                        <select
                          value={filterSubType}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeSubTypeFilter(event.target.value)
                          }
                        >
                          <option value="subscription">{t("yes")}</option>
                          <option value="non-subscription">{t("no")}</option>
                        </select>
                      </div>
                    </Col>
                    {/* <Col sm={4} className="mb-2">
                      <label className="form-label">{t("gender")}</label>
                      <div className="search-box">
                        <select
                          value={filterGenderId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeGenderFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByGender")}</option>
                          <option value="Female">{t("female")}</option>
                          <option value="Male">{t("male")}</option>
                          <option value="Unspecified">
                            {t("unspecified")}
                          </option>
                        </select>
                      </div>
                    </Col> */}

                    <React.Fragment>
                      <>
                        <Col sm={4} className="mb-2">
                          <label className="form-label">{t("course")}</label>
                          <div className="search-box">
                            <CourseSelect
                              value={filterCourseId}
                              placeholder={t("filterByCourse")}
                              type="select"
                              id="courseId"
                              name="courseId"
                              className="form-select"
                              aria-label={t("filterByCourse")}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => onChangeCourseFilter(event.target.value)}
                            ></CourseSelect>
                          </div>
                        </Col>
                        <Col sm={4} className="mb-2">
                          <label className="form-label">{t("grade")}</label>
                          <div className="search-box">
                            <GradeSelect
                              courseId={filterCourseId}
                              value={filterGradeId}
                              placeholder={t("filterByGrade")}
                              type="select"
                              id="gradeId"
                              name="gradeId"
                              className="form-select"
                              aria-label={t("filterByGrade")}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                              ) => onChangeGradeFilter(event.target.value)}
                            ></GradeSelect>
                          </div>
                        </Col>
                      </>
                    </React.Fragment>
                  </Row>
                  <Row>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <ActivityButton
                          title="download"
                          inProgressTitle="downloading"
                          color="success"
                          className="btn btn-success"
                          onClick={generateReport}
                          inProgress={isLoadingReport}
                          disabled={isLoadingReport}
                        ></ActivityButton>
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
